import {global, lm} from "../appContext";
import {ICompanySyncstep, IMessage, IRuleset} from "../index";
import {MAX_RESULTS_PER_PAGE} from "../constants";
import {_} from 'lodash';
import {nsSyncstep} from "../component/sync-steps/fn";
import {nsClone} from "./fn";
import PromiseRequest from "../req/PromiseRequest";
import {nsRuleset} from "../component/ruleset/nsRuleset";
import pubsub from "../pubsub";
import {ICloneDialogOptions} from "./index";

export namespace nsMessageFlowClone {

    /**
     * Persists a message flow already prepared for the target org (sync step, ruleset link, body encoding, name).
     * Used by the message flow copy wizard after rulesets are copied and names are resolved; keeps the same
     * `createmessage` / `getmessage` contract as {@link create}.
     */
    export function persistPreparedCopy(cpy: IMessage, trgOrgNr: string): Promise<IMessage> {
        const payload = _.cloneDeep(cpy);
        payload.body = encodeURIComponent(payload.body);
        return new PromiseRequest("createmessage")
            .request({
                jmessage: JSON.stringify(payload),
                orgnr: trgOrgNr,
            })
            .then((data: IMessage[]) => {
                global.cacheManager.removeCache("getruleset");
                global.cacheManager.removeCache("searchruleset");
                global.cacheManager.removeCache("searchmessage");
                return data[0]!;
            })
            .then((created) =>
                new PromiseRequest("getmessage").request({
                    orgnr: trgOrgNr,
                    id: created.id,
                }),
            )
            .then((data: [IMessage]) => data[0]!);
    }

    export function create(src: IMessage, trgOrgNr: string, params: any = {}): Promise<IMessage[]> {
        const srcOrgNr = src.companyid;
        let cpy: IMessage = _.extend(_.cloneDeep(src), params);

        // id will be created
        cpy.id = 0;
        cpy.companyid = trgOrgNr;
        cpy.active = false
        cpy.body = encodeURIComponent(cpy.body)

        // Execute promises
        return Promise.resolve()
            .then(() => {
                // --- COMPANY SYNCSTEP --------------------------------------------------------------------------------
                return nsSyncstep.getAll({orgnr: trgOrgNr})
                    .then((data: ICompanySyncstep[]) => {
                        if (!data.length) {
                            throw lm.t('message: no syncsteps defined for target organization')
                        }
                        const sorted = _.sortBy(data, 'order');
                        return _.last(sorted);
                    })
                    .then((syncstep: ICompanySyncstep) => {
                        cpy.syncstepname = syncstep.syncstep.name;
                        cpy.syncstepid = syncstep.syncstep.id;
                    });
            })
            .then(() => {
                // --- RULESET -----------------------------------------------------------------------------------------
                if (cpy.rulesetname) {
                    return nsRuleset.find(cpy.rulesetname, trgOrgNr)
                        .then((data: IRuleset[]) => {
                            // find existing within target org
                            const ruleset = data[0];
                            if (ruleset) {
                                return [ruleset];
                            } else {
                                // find existing within src org and create it in the trg org
                                return nsRuleset
                                    .find(cpy.rulesetname, srcOrgNr)
                                    .then((data: IRuleset[]) => {
                                        const first = data[0];
                                        if (!first) {
                                            throw new Error('ruleset missing');
                                        }
                                        return nsRuleset.create(first, trgOrgNr);
                                    });
                            }
                        })
                        .then((data: IRuleset[]) => {
                            const first = data[0];
                            if (!first) {
                                throw new Error('ruleset missing');
                            }
                            cpy.rulesetid = first.id;
                            cpy.rulesetname = first.name;
                        });
                }
            })
            .then(() => {
                // --- MESSAGE TEMPLATE --------------------------------------------------------------------------------
                return new PromiseRequest("createmessage")
                    .request({
                        jmessage: JSON.stringify(cpy),
                        orgnr: trgOrgNr
                    })
                    .then((data: IMessage[]) => {
                        global.cacheManager.removeCache('getruleset');
                        global.cacheManager.removeCache('searchruleset');
                        global.cacheManager.removeCache("searchmessage");
                        // replace the clone with the newly created message template
                        cpy = data[0]!;
                    });
            })
            .then(() => {
                return new PromiseRequest("getmessage")
                    .request({
                        orgnr: trgOrgNr,
                        id: cpy.id
                    });
            });
    }

    export function trash(messageTemplate: IMessage): Promise<any> {
        return new PromiseRequest("deletemessage")
            .request({id: messageTemplate.id})
            .then(() => {
                global.cacheManager.removeCache('getruleset');
                global.cacheManager.removeCache('searchruleset');
                global.cacheManager.removeCache("searchmessage");
                global.cacheManager.removeCache("getmessage");
                pubsub.trigger('delete.messageflow.complete');
            });
    }

    const DialogOptions: ICloneDialogOptions = {icon: 'fa-message-flow', translationKey: 'messageflow'};

    export function copyDialog(messageTemplate: IMessage) {
        nsClone.dialogCopy(DialogOptions, "component/messages/copy-wizard/main", messageTemplate);
    }

    export function trashDialog(messageTemplate: IMessage) {
        nsClone.dialogDelete(DialogOptions, "component/messages/dialog/delete", messageTemplate);
    }

    export function getAll() {
        return new PromiseRequest("searchmessage")
            .request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr: global.login.selectedOrganizationId(),
                sortcol: "name",
                asc: true
            })
    }

}
